import { type FunctionComponent, useContext, useEffect } from 'react';

import { useHasRole } from '~/shared/context/use-has-role';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';

import { MixpanelTrackingContext } from '../../tracking/mixpanel/MixpanelTrackingProvider';
import { useChatComposerActive, useChatComposerCallbacks } from '../chat-composer-contexts';
import {
    resolveStarterPromptContext,
    type StarterPromptContext,
    type StarterPromptKey,
    selectStarterPrompts,
} from '../starter-prompts';

type StarterPromptSurface = 'expanded' | 'pill';

type StarterPromptChipsProps = {
    readonly onImpression: (keys: readonly StarterPromptKey[]) => void;
    readonly onPick: (key: StarterPromptKey, text: string) => void;
    readonly promptKeys: readonly StarterPromptKey[];
    readonly surface: StarterPromptSurface;
};

export const StarterPromptChips: FunctionComponent<StarterPromptChipsProps> = ({
    onImpression,
    onPick,
    promptKeys,
    surface,
}) => {
    const translate = useTranslate();
    const keysSignature = promptKeys.join(',');

    // biome-ignore lint/correctness/useExhaustiveDependencies: impressions fire once per distinct prompt set
    useEffect(() => {
        if (promptKeys.length > 0) onImpression(promptKeys);
    }, [keysSignature]);

    if (promptKeys.length === 0) return null;

    if (surface === 'pill') {
        return (
            <ul aria-label={translate('chat.starter-prompts.label')} className="m-0 flex list-none flex-col p-xs">
                {promptKeys.map((key) => {
                    const text = translate(`chat.starter-prompts.items.${key}`);
                    return (
                        <li key={key}>
                            <Button
                                className="w-full justify-start text-body-xs"
                                size="sm"
                                title={text}
                                variant="tertiary"
                                onClick={() => onPick(key, text)}
                            >
                                <span className="truncate">{text}</span>
                            </Button>
                        </li>
                    );
                })}
            </ul>
        );
    }

    return (
        <ul aria-label={translate('chat.starter-prompts.label')} className="m-0 flex list-none flex-wrap gap-s p-0">
            {promptKeys.map((key) => {
                const text = translate(`chat.starter-prompts.items.${key}`);
                return (
                    <li key={key}>
                        <Button
                            className="!min-h-0 !rounded-xl text-left text-body-xs whitespace-normal"
                            size="sm"
                            variant="secondary"
                            onClick={() => onPick(key, text)}
                        >
                            {text}
                        </Button>
                    </li>
                );
            })}
        </ul>
    );
};

type ChatStarterPromptsProps = {
    readonly onPicked?: () => void;
    readonly surface: StarterPromptSurface;
};

/** Curated prompts for the empty chat: picks a set from the page context and the user's edit rights, and fills the composer on click. */
export const ChatStarterPrompts: FunctionComponent<ChatStarterPromptsProps> = ({ onPicked, surface }) => {
    const { resolvedChatContext } = useChatComposerActive();
    const { fillDraft } = useChatComposerCallbacks();
    const tracking = useContext(MixpanelTrackingContext);
    const canEdit = useHasRole('job_architect');

    const context: StarterPromptContext = resolveStarterPromptContext(resolvedChatContext);
    const promptKeys = selectStarterPrompts(context, canEdit);

    return (
        <StarterPromptChips
            promptKeys={promptKeys}
            surface={surface}
            onImpression={(keys) => {
                for (const promptKey of keys) {
                    tracking?.trackEvent({
                        name: 'chat-starter-prompt',
                        payload: { action: 'impression', contextKind: context, promptKey, surface },
                    });
                }
            }}
            onPick={(promptKey, text) => {
                tracking?.trackEvent({
                    name: 'chat-starter-prompt',
                    payload: { action: 'click', contextKind: context, promptKey, surface },
                });
                fillDraft(text);
                onPicked?.();
            }}
        />
    );
};
